import { AxiosInstance } from 'axios';
import { CreateProjectParams, ImageDTO, ProjectDTO, SaveImageParams, textDTO, levelDTO, UserProjectDTO } from '../../types/apiType';
import { UpdateUserProjectParams } from '../../../views/dashboard/project/components/ProjectItem/ProjectItem';

import { PaginatedConnection, PaginatedQueryParams } from '../../types/common';
import PythonApiClient from '../../pythonApiClient';

export default class ProjectService {
  private pythonApi = PythonApiClient;

  constructor(private axios: AxiosInstance) {}

  /* User services */

  getListProject = async (params?: PaginatedQueryParams): Promise<PaginatedConnection<ProjectDTO>> => {
    const { data } = await this.axios.get('project', { params });
    return data;
  };

  countProjectByStatus = async (status: string): Promise<number> => {
    const { data } = await this.axios.get('project/status', { params: { status } });
    return data;
  };

  getShapeData = async (params: { imageId: string; userId?: string }): Promise<string> => {
    const { data } = await this.axios.get('project/shape', { params });
    return data;
  };

  getAdminID = async (params: { userProjectId: string; userId: string }): Promise<string> => {
    const { data } = await this.axios.get('project/shape', { params });
    return data;
  };

  // add new notification
  getNewNotification = async (projectId: string): Promise<number> => {
    const { data } = await this.pythonApi.get('notificationTes', { params: { id: projectId } });
    return data;
  };

  /* */
  getShapeData2 = async (params: { imageId: string; userId?: string }): Promise<string> => {
    const { data } = await this.pythonApi.put('/loadJSONGT', params);
    return data;
  };

  getTotalCompletedImage = async (projectId: string): Promise<number> => {
    const { data } = await this.axios.get(`project/image/total-completed/${projectId}`);
    return data;
  };

  // 채점 결과 저장 (annotation.gtScore)
  saveScore = async (params: {
    projectId: string;
    imageId: string;
    score: number;
    grade?: unknown;
    feedback?: unknown;
    userId?: string;
  }): Promise<{ id: string; gtScore: number }> => {
    const { data } = await this.axios.post('project/score', params);
    return data;
  };

  // 사용자별 프로젝트 평균 등급 (annotation.gtScore 집계, testScoreSolo2 대체)
  getUserScores = async (userId?: string): Promise<Record<string, string | number>> => {
    const { data } = await this.axios.get('project/score/user', { params: { userId } });
    return data;
  };

  /** 대시보드 ProjectItem 호환: JSON 문자열로 반환 */
  getUserScoresJson = async (userId?: string): Promise<string | null> => {
    if (!userId) return null;
    const scores = await this.getUserScores(userId);
    return JSON.stringify(scores);
  };

  getSessionResult = async (projectId: string, userId: string): Promise<{
    completed: number;
    avgScore: number;
    targetCompleted: number;
    targetScore: number;
    totalCompletedImages: number;
  }> => {
    const { data } = await this.axios.get(`project/session-result/${projectId}?userId=${userId}`);
    return data;
  };

  getProjectById = async (
    id: string,
    limit?: number,
    userId?: string,
    onlyIncomplete?: boolean,
    imageIds?: string[],
    gtEditor?: boolean,
  ): Promise<ProjectDTO> => {
    const params: string[] = [];
    if (imageIds && imageIds.length > 0) {
      params.push(`imageIds=${imageIds.join(',')}`);
    } else {
      if (limit) params.push(`limit=${limit}`);
      if (onlyIncomplete) params.push(`onlyIncomplete=true`);
    }
    if (userId) params.push(`userId=${userId}`);
    if (gtEditor) params.push('gtEditor=true');
    const queryString = params.length > 0 ? `?${params.join('&')}` : '';
    const { data } = await this.axios.get(`project/${id}${queryString}`);
    const res = {
      ...data,
      images: data.images.map((image: any) => {
        const newImage: ImageDTO = { ...image, shapeData: JSON.parse(image.shapeData)};
        return newImage;
      }),
    };
    console.log(res);
    return res;
  };

  public getProjectListByCategory = async (category: string): Promise<ProjectDTO[]> => {
    const { data } = await this.axios.get(`project/category/${category}`);
    const res = {
      ...data,
    };
    return res;
  };

  saveProject = async (query: {
    id: string;
    params: { userId?: string; completeAll: boolean; isGtEditor?: boolean; data: SaveImageParams[] };
  }) => {
    const { data } = await this.axios.put(`project/save/${query.id}`, query.params);
    return data;
  };

  saveProject2 = async (query: { id: string; params: { completeAll: boolean; data: SaveImageParams[] } }) => {
    const { data } = await this.pythonApi.put('/saveJSONGT', query.params);
    return data;
  };

  getProjectGTData = async (query: { level: string }) => {
    const { data } = await this.pythonApi.put(`/loadJSONGT`, query.level);
    return data;
  };

  getProjectGTData2 = async (query: { userId: string }) => {
    const { data } = await this.axios.get(`/shape?userProjectId=`);
    return data;
  };

  saveProjectGTData = async (query: { level: string }) => {
    const { data } = await this.pythonApi.put(`/saveJSONGT`, query.level);
    return data;
  };

  getProjectShowScoreAdmin = async (): Promise<textDTO> => {
    const { data } = await this.pythonApi.put(`/testScoreAdmin`);
    return data;
  };

  getProjectShowScoreSolo = async (id: string): Promise<textDTO> => {
    const { data } = await this.pythonApi.get(`/testScoreSolo?id=${id}`);
    return data;
  };

  /** @deprecated testScoreSolo2(txt) 대신 getUserScoresJson 사용 */
  getProjectShowScoreSolo2 = async (userId: string) => this.getUserScoresJson(userId);

  getCountUngradedProjects = async (id: string) : Promise<{count: number, data : any}>=> {
    const { data } = await this.axios.get(`userproject/count?userId=${id}`);
    return data;
  };

  // 채점용 resData: 현재 이미지 GT만 포함 (프로젝트 전체 shape 전송 방지)
  private buildScoringResData = (
    res: any[],
    imgName?: string,
    imageId?: string,
  ): any[] => {
    return res.map((r: any) => ({
      ...r,
      userData: (r.userData || [])
        .filter((d: any) => {
          if (imgName && d.name === imgName) return true;
          if (imageId && d.id === imageId) return true;
          return !imgName && !imageId;
        })
        .map((d: any) => ({
          ...d,
          shapeData: typeof d.shapeData === 'string' ? JSON.parse(d.shapeData) : d.shapeData,
        })),
    }));
  };

  /* check tutorial */
  checkTutorial = async (query: {
    id: string;
    adminId: string;
    scoredUserId?: string;
    params: {
      completeAll: boolean;
      data: SaveImageParams[];
      imgidx?: number;
      imgName?: string;
      profile?: boolean;
    };
  }): Promise<null> => {
    const profile =
      (typeof window !== 'undefined' && window.localStorage?.getItem('scoringProfile') === '1')
      || process.env.REACT_APP_SCORING_PROFILE === '1';
    const tTotal = Date.now();

    const imgIdx = query.params.imgidx ?? 0;
    const targetImage = query.params.data?.[imgIdx];

    // GT: 관리자 user.id(adminId) 기준 shapes (예전 방식)
    const tGt = Date.now();
    const imageId = targetImage?.imageId;
    const adminId = query.adminId || undefined;
    const res = await this.findProjectShapeData(query.id, adminId, imageId);
    const gtMs = Date.now() - tGt;

    const tBuild = Date.now();
    const resData = this.buildScoringResData(res, query.params.imgName, targetImage?.imageId);
    const buildMs = Date.now() - tBuild;

    const jsonSend = { ...query.params, resData, profile: profile || undefined };

    const tPy = Date.now();
    const { data } = await this.pythonApi.put(`/tesBoundingScore`, jsonSend);
    const pyMs = Date.now() - tPy;

    if (profile) {
      const resDataKb = Math.round(JSON.stringify(resData).length / 1024);
      // eslint-disable-next-line no-console
      console.log('[SCORING_PROFILE] --- checkTutorial (browser) ---');
      // eslint-disable-next-line no-console
      console.log(`[SCORING_PROFILE] GT fetch (adminId=${adminId ?? 'none'}): ${gtMs}ms (gtUsers=${res.length})`);
      // eslint-disable-next-line no-console
      console.log(`[SCORING_PROFILE] buildScoringResData: ${buildMs}ms (resData ~${resDataKb}KB)`);
      // eslint-disable-next-line no-console
      console.log(`[SCORING_PROFILE] python tesBoundingScore: ${pyMs}ms`);
      // eslint-disable-next-line no-console
      console.log(`[SCORING_PROFILE] checkTutorial TOTAL: ${Date.now() - tTotal}ms`);
      // eslint-disable-next-line no-console
      console.log('[SCORING_PROFILE] --- checkTutorial 끝 (Python 상세는 채점 서버 콘솔) ---');
    }

    return {
      ...data,
      imagePath: targetImage?.imagePath,
    };
  };

  /* check tutorial */
  autoLabel = async (query:
    {
      params: { polygon?: string; box?: string; tagType: string; path: string };
    }): Promise<null> => {
    const { data } = await this.pythonApi.post(`/autoLabeling`, query, { timeout: 999999999 });
    return data;
  };
  /* end */

  saveTest = async (query: { usn?: string; id: string; adminId: string; params: { data: SaveImageParams[] } }): Promise<null> => {
    const res = await this.findProjectShapeData(query.id, query.adminId);
    console.log({res})
    const resData = res.map((r: any) => ({ ...r, userData: r.userData.map((d: any) => ({ ...d, shapeData: JSON.parse(d.shapeData) })) }));

    const jsonSend = {...query, resData};
    const { data } = await this.pythonApi.put(`/testSave`, jsonSend);
    return data;
  };

  /* Admin services */

  getListProjectAdmin = async (params?: PaginatedQueryParams): Promise<PaginatedConnection<ProjectDTO>> => {
    console.log("CALL THIS")
    const { data } = await this.axios.get('project/admin', { params });
    return data;
  };

  countProjectByStatusAdmin = async (status: string, gt?: string): Promise<number> => {
    const { data } = await this.axios.get('project/admin/status', { params: { status, gt} });
    return data;
  };

  getAllProjectAdmin = async (): Promise<ProjectDTO[]> => {
    const { data } = await this.axios.get('project/admin/all');
    return data;
  };

  getProjectByIdAdmin = async (params: { id: string; isAll: boolean }): Promise<ProjectDTO> => {
    const { data } = await this.axios.get(`project/admin/${params.id}`, { params: { isAll: params.isAll } });
    const res = {
      ...data,
      workers: data.workers.map((worker: any) => {
        const newWorker = {
          ...worker,
          userImage: worker.userImage.map((userImg: any) => {
            const newImg: ImageDTO = { ...userImg, shapeData: JSON.parse(userImg.shapeData) };
            return newImg;
          }),
        };
        return newWorker;
      }),
    };
    return res;
  };

  getUserProject = async (id: string): Promise<UserProjectDTO> => {
    const { data } = await this.axios.get(`userproject/${id}`);
    return data;
  };

  createProject = async (params: CreateProjectParams): Promise<null> => {
    const { data } = await this.axios.post('project', params);
    return data;
  };

  findProjectShapeData = async (id: string, adminId?: string, imageId?: string): Promise<any> => {
    const { data } = await this.axios.post(`project/shape/${id}`, { adminId, imageId });
    return data;
  };

  findProjectShapeDataForScoring = async (id: string, imageId: string, adminId: string): Promise<any> => {
    const { data } = await this.axios.post(`project/shape/${id}/scoring`, { imageId, adminId });
    return data;
  };

  refreshData = async (): Promise<any> => {
    const { data } = await this.axios.get(`debug/refresh`, { timeout: 999999999 });
    return data;
  };

  updateProject = async (params: { id: string; body: CreateProjectParams }): Promise<null> => {
    const { data } = await this.axios.put(`project/${params.id}`, params.body);
    return data;
  };

  updateStatus = async (params: { id: string; body: { status: string } }): Promise<null> => {
    const { data } = await this.axios.put(`project/status/${params.id}`, params.body);
    return data;
  };

  updateUserProject = async (params: UpdateUserProjectParams): Promise<null> => {
    console.log("UPDATE USER PROJECT CALLED")
    const { data } = await this.axios.post(`project/user/`, params);
    return data;
  };

  uploadImage = async (image: File, callback: (progress: number) => void): Promise<ImageDTO> => {
    const bodyFormData = new FormData();
    bodyFormData.append('image', image);
    const { data } = await this.axios.post('project/image', bodyFormData, {
      onUploadProgress: (data) => {
        callback(Math.round((100 * data.loaded) / image.size));
      },
    });
    return data;
  };

  deleteImage = async (id: string): Promise<null> => {
    const { data } = await this.axios.delete(`project/image/${id}`);
    return data;
  };

  deleteUnUseImage = async (): Promise<null> => {
    const { data } = await this.axios.delete(`project/image/all`);
    return data;
  };

  deleteProject = async (id: string): Promise<null> => {
    const { data } = await this.axios.delete(`project/${id}`);
    return data;
  };
}
